import React, { useState, useMemo } from "react";
import {
  FiSearch, FiFilter, FiX, FiChevronRight, FiDownload,
  FiRefreshCw, FiTrash2, FiEdit3, FiZap, FiWifi,
  FiAlertTriangle, FiServer, FiActivity, FiCpu,
  FiCheckCircle, FiXCircle, FiAlertOctagon, FiClock,
  FiMapPin, FiRadio, FiBluetooth, FiPackage,
} from "react-icons/fi";
import { MdSettingsInputAntenna } from "react-icons/md";
import { allDevices } from "../../../Data/DeviceData";
import { siteDummyData } from "../../../Data/SiteData";
import { useSite } from "../../../context/SiteContext";
import {
  statusMeta, connMeta, roleMeta, healthColor, healthLabel,
  HealthRing, BatteryBar, SignalBars, DvcBadge, SeverityBadge, solColor,
} from "./deviceHelpers";

const ALL_SOLUTIONS  = ["All", "STP", "AQM", "Irrigation", "Water", "Facility", "Fleet", "None"];
const ALL_ROLES      = ["All", "Gateway", "Sensor", "Controller"];
const ALL_CONN       = ["All", "LoRa", "WiFi", "Ethernet", "Cellular", "Bluetooth", "Modbus"];
const ALL_STATUS     = ["All", "Online", "Warning", "Error", "Offline"];

// Site name lookup helper
const siteNameById = (siteId) => {
  const s = siteDummyData.sites.find(x => x.id === siteId);
  return s ? s.name : siteId;
};

// ── Device Detail Panel ───────────────────────────────────────────────────────
const DeviceDetail = ({ device, onClose, allDevs }) => {
  const [tab, setTab] = useState("info");
  if (!device) return null;

  const sm = statusMeta(device.status);
  const cm = connMeta(device.connectivity);
  const rm = roleMeta(device.role);
  const sc = solColor(device.assignedSolution);
  const hc = healthColor(device.healthScore);

  const gatewayDev = device.gatewayId ? allDevs.find(x => x.id === device.gatewayId) : null;
  const children   = device.childDeviceIds ? device.childDeviceIds.map(id => allDevs.find(x => x.id === id)).filter(Boolean) : [];

  const connDetails = device.loraDetails || device.wifiDetails || device.ethernetDetails || device.cellularDetails || device.bluetoothDetails || device.modbusDetails || null;

  // Health score breakdown
  const hBreakdown = [
    { label: "Heartbeat",    score: device.status === "Online" ? 25 : device.status === "Warning" ? 15 : 0, max: 25 },
    { label: "Signal",       score: device.signal !== null ? Math.max(0, Math.round(((device.signal + 120) / 70) * 25)) : 25, max: 25 },
    { label: "Battery",      score: device.battery !== null ? Math.round((device.battery / 100) * 20) : 20, max: 20 },
    { label: "Packet Loss",  score: device.loraDetails ? Math.round((1 - device.loraDetails.packetLoss / 100) * 15) : 15, max: 15 },
    { label: "Error Rate",   score: device.status === "Error" ? 0 : device.status === "Warning" ? 7 : 15, max: 15 },
  ];

  return (
    <div className="dvc-detail-panel">
      <div className="dvc-detail-header">
        <div className="dvc-detail-title-row">
          <div className="dvc-detail-role-dot" style={{ background: rm.color }}>{rm.letter}</div>
          <div>
            <div className="dvc-detail-name">{device.name}</div>
            <div className="dvc-detail-id">{device.id} · {device.type}</div>
          </div>
          <button className="dvc-close-btn" onClick={onClose}><FiX size={18} /></button>
        </div>
        <div className="dvc-detail-badges">
          <span className="dvc-status-badge" style={{ background: sm.bg, color: sm.color }}>
            <sm.icon size={12} /> {device.status}
          </span>
          <DvcBadge text={device.connectivity} color={cm.color} bg={cm.bg} />
          {device.assignedSolution && <DvcBadge text={device.assignedSolution} color={sc.color} bg={sc.bg} />}
        </div>
        <HealthRing score={device.healthScore} size={60} />
      </div>

      {/* Sub-tabs */}
      <div className="dvc-detail-tabs">
        {["info","health","connectivity","telemetry","controls","events"].map(t => (
          <button key={t} className={`dvc-detail-tab ${tab === t ? "dvc-detail-tab-active" : ""}`}
            onClick={() => setTab(t)}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
        ))}
      </div>

      <div className="dvc-detail-body">
        {/* Info */}
        {tab === "info" && (
          <div className="dvc-info-grid">
            {[
              { label: "Device ID",       val: device.id },
              { label: "Serial Number",   val: device.serialNumber },
              { label: "Model",           val: device.model },
              { label: "Make",            val: device.make },
              { label: "Firmware",        val: device.firmware },
              { label: "Site",            val: device.site },
              { label: "Solution",        val: device.assignedSolution },
              { label: "Installed On",    val: device.installedOn },
              { label: "Last Seen",       val: device.lastSeen },
              { label: "Gateway",         val: gatewayDev ? `${gatewayDev.name} (${gatewayDev.id})` : device.role === "Gateway" ? "— (This is gateway)" : "Direct" },
            ].map(r => (
              <div key={r.label} className="dvc-info-row">
                <div className="dvc-info-lbl">{r.label}</div>
                <div className="dvc-info-val">{r.val || "—"}</div>
              </div>
            ))}
            {children.length > 0 && (
              <div className="dvc-info-row" style={{ gridColumn: "1/-1" }}>
                <div className="dvc-info-lbl">Child Devices ({children.length})</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {children.map(c => {
                    const csm = statusMeta(c.status);
                    return (
                      <span key={c.id} style={{ padding: "3px 8px", borderRadius: 12, background: csm.bg, color: csm.color, fontSize: 11, fontWeight: 600 }}>
                        {c.id}: {c.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Health */}
        {tab === "health" && (
          <div>
            <div className="dvc-health-score-row">
              <HealthRing score={device.healthScore} size={80} />
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: hc }}>{healthLabel(device.healthScore)}</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Overall Health Score: {device.healthScore}/100</div>
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              {hBreakdown.map(h => (
                <div key={h.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 100, fontSize: 12, color: "#374151", fontWeight: 500 }}>{h.label}</div>
                  <div style={{ flex: 1, height: 10, background: "#e5e7eb", borderRadius: 5, overflow: "hidden" }}>
                    <div style={{ width: `${(h.score / h.max) * 100}%`, height: "100%", background: h.score / h.max > 0.7 ? "#10b981" : h.score / h.max > 0.4 ? "#f59e0b" : "#ef4444", borderRadius: 5 }} />
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", minWidth: 40, textAlign: "right" }}>{h.score}/{h.max}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, padding: 10, background: "#f8fafc", borderRadius: 8, fontSize: 12, color: "#6b7280" }}>
              🤖 AI: {device.healthScore >= 85 ? "Device is performing optimally. No action required." : device.healthScore >= 60 ? "Device health is fair. Monitor signal quality and battery levels." : device.healthScore >= 30 ? "Device health is degraded. Inspect connectivity and battery. Consider field visit." : "Device health critical. Immediate attention required — device may fail soon."}
            </div>
          </div>
        )}

        {/* Connectivity */}
        {tab === "connectivity" && (
          <div>
            <div className="dvc-info-grid">
              <div className="dvc-info-row">
                <div className="dvc-info-lbl">Type</div>
                <div className="dvc-info-val" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <cm.Icon size={14} color={cm.color} />{device.connectivity}
                </div>
              </div>
              {device.ip && <><div className="dvc-info-row"><div className="dvc-info-lbl">IP Address</div><div className="dvc-info-val">{device.ip}</div></div></>}
              {device.mac && <><div className="dvc-info-row"><div className="dvc-info-lbl">MAC Address</div><div className="dvc-info-val" style={{ fontFamily: "monospace" }}>{device.mac}</div></div></>}
            </div>
            {connDetails && (
              <div style={{ marginTop: 12 }}>
                <div className="dvc-section-title">Protocol Details</div>
                <div className="dvc-info-grid" style={{ marginTop: 8 }}>
                  {Object.entries(connDetails).filter(([k]) => k !== "packetsToday").map(([k, v]) => (
                    <div key={k} className="dvc-info-row">
                      <div className="dvc-info-lbl">{k.replace(/([A-Z])/g, " $1").trim()}</div>
                      <div className="dvc-info-val" style={{ fontFamily: typeof v === "string" && v.includes(":") ? "monospace" : "inherit" }}>
                        {String(v)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <div className="dvc-section-title">Signal Quality</div>
              <div style={{ marginTop: 8, display: "flex", gap: 20 }}>
                <div><SignalBars rssi={device.signal} /></div>
                <div><BatteryBar pct={device.battery} /></div>
              </div>
            </div>
            {device.loraDetails && (
              <div style={{ marginTop: 12, padding: 10, background: "#f0fdf4", borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>LoRa Diagnostics</div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <div><span style={{ color: "#6b7280", fontSize: 11 }}>Packets Today</span><div style={{ fontSize: 15, fontWeight: 700, color: "#667eea" }}>{device.loraDetails.packetsToday}</div></div>
                  <div><span style={{ color: "#6b7280", fontSize: 11 }}>Packet Loss</span><div style={{ fontSize: 15, fontWeight: 700, color: device.loraDetails.packetLoss > 5 ? "#ef4444" : "#10b981" }}>{device.loraDetails.packetLoss}%</div></div>
                  <div><span style={{ color: "#6b7280", fontSize: 11 }}>SF</span><div style={{ fontSize: 15, fontWeight: 700, color: "#374151" }}>{device.loraDetails.sf}</div></div>
                  <div><span style={{ color: "#6b7280", fontSize: 11 }}>BW</span><div style={{ fontSize: 15, fontWeight: 700, color: "#374151" }}>{device.loraDetails.bandwidth}</div></div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Telemetry */}
        {tab === "telemetry" && (
          <div>
            {device.telemetry && Object.keys(device.telemetry).length > 0 ? (
              <div className="dvc-telemetry-grid">
                {Object.entries(device.telemetry).filter(([, v]) => v !== null).map(([k, v]) => (
                  <div key={k} className="dvc-tele-card">
                    <div className="dvc-tele-val">{typeof v === "number" ? v : String(v)}</div>
                    <div className="dvc-tele-lbl">{k.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim()}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="dvc-empty">No telemetry data available</div>
            )}
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 12 }}>Last updated: {device.lastSeen}</div>
          </div>
        )}

        {/* Controls */}
        {tab === "controls" && (
          <div>
            {device.controls ? (
              <div>
                <div className="dvc-info-grid">
                  {Object.entries(device.controls).map(([k, v]) => (
                    <div key={k} className="dvc-info-row">
                      <div className="dvc-info-lbl">{k.replace(/([A-Z])/g, " $1").trim()}</div>
                      <div className="dvc-info-val">{String(v)}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="dvc-ctrl-btn dvc-ctrl-blue"><FiRefreshCw size={13} /> Restart Device</button>
                  <button className="dvc-ctrl-btn dvc-ctrl-green"><FiZap size={13} /> Send Command</button>
                  <button className="dvc-ctrl-btn dvc-ctrl-orange"><FiEdit3 size={13} /> Edit Config</button>
                  <button className="dvc-ctrl-btn dvc-ctrl-purple"><FiDownload size={13} /> Update Firmware</button>
                </div>
              </div>
            ) : (
              <div>
                <div className="dvc-empty" style={{ marginBottom: 16 }}>No direct controls available for this device type</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="dvc-ctrl-btn dvc-ctrl-blue"><FiRefreshCw size={13} /> Restart</button>
                  <button className="dvc-ctrl-btn dvc-ctrl-purple"><FiDownload size={13} /> Update Firmware</button>
                  <button className="dvc-ctrl-btn dvc-ctrl-orange"><FiEdit3 size={13} /> Reconfigure</button>
                </div>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <div className="dvc-section-title">Assignment</div>
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                <button className="dvc-ctrl-btn dvc-ctrl-green" style={{ gap: 5 }}>
                  <FiMapPin size={13} /> Reassign Solution
                </button>
                <button className="dvc-ctrl-btn dvc-ctrl-orange" style={{ gap: 5 }}>
                  <FiServer size={13} /> Change Gateway
                </button>
                <button className="dvc-ctrl-btn" style={{ background: "#fee2e2", color: "#ef4444", border: "1px solid #fca5a5" }}>
                  <FiTrash2 size={13} /> Decommission
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Events */}
        {tab === "events" && (
          <div>
            <div className="dvc-section-title">Recent Device Events</div>
            <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              {(device.events || []).map((ev, i) => (
                <div key={i} className="dvc-event-row">
                  <div className="dvc-ev-dot" style={{ background: ev.type === "Error" ? "#ef4444" : ev.type === "Warning" ? "#f59e0b" : "#10b981" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: "#374151" }}>{ev.msg}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{ev.time}</div>
                  </div>
                  <span className="dvc-ev-type" style={{ background: ev.type === "Error" ? "#fee2e2" : ev.type === "Warning" ? "#fef3c7" : "#d1fae5", color: ev.type === "Error" ? "#ef4444" : ev.type === "Warning" ? "#f59e0b" : "#10b981" }}>{ev.type}</span>
                </div>
              ))}
              {(!device.events || device.events.length === 0) && <div className="dvc-empty">No events recorded</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SOLUTIONS = ["STP", "AQM", "Irrigation", "Water", "Facility", "Fleet", "None"];
const EXPORT_FORMATS = ["CSV", "JSON", "PDF"];

// ── Inline Batch Dialogs ───────────────────────────────────────────────────────
const BatchAssignDialog = ({ selectedIds, allDevs, onClose }) => {
  const [solution, setSolution] = useState("STP");
  const devices = allDevs.filter(d => selectedIds.includes(d.id));
  return (
    <div className="dvc-batch-dialog">
      <div className="dvc-batch-dialog-header">
        <FiMapPin size={14} color="#3b82f6" />
        <span>Assign {selectedIds.length} device{selectedIds.length > 1 ? "s" : ""} to Solution</span>
        <button className="dvc-close-btn" style={{ marginLeft: "auto" }} onClick={onClose}><FiX size={16} /></button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
        <label style={{ fontSize: 13, color: "#374151", fontWeight: 500, whiteSpace: "nowrap" }}>Target Solution:</label>
        <select className="dvc-filter-sel" value={solution} onChange={e => setSolution(e.target.value)} style={{ flex: 1 }}>
          {SOLUTIONS.map(s => <option key={s}>{s}</option>)}
        </select>
        <button className="dvc-ctrl-btn dvc-ctrl-green" onClick={onClose}>
          <FiMapPin size={13} /> Assign to {solution}
        </button>
      </div>
      <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
        {devices.map(d => (
          <span key={d.id} style={{ padding: "2px 8px", background: "#ede9fe", color: "#7c3aed", borderRadius: 8, fontSize: 11, fontWeight: 600 }}>{d.name}</span>
        ))}
      </div>
    </div>
  );
};

const BatchFirmwareDialog = ({ selectedIds, allDevs, onClose }) => {
  const [version, setVersion] = useState("v2.5.0");
  const devices = allDevs.filter(d => selectedIds.includes(d.id));
  return (
    <div className="dvc-batch-dialog">
      <div className="dvc-batch-dialog-header">
        <FiDownload size={14} color="#8b5cf6" />
        <span>Bulk Firmware Update — {selectedIds.length} device{selectedIds.length > 1 ? "s" : ""}</span>
        <button className="dvc-close-btn" style={{ marginLeft: "auto" }} onClick={onClose}><FiX size={16} /></button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
        <label style={{ fontSize: 13, color: "#374151", fontWeight: 500, whiteSpace: "nowrap" }}>Target Version:</label>
        <input className="dvc-search" value={version} onChange={e => setVersion(e.target.value)}
          style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 8, padding: "5px 10px" }} />
        <button className="dvc-ctrl-btn dvc-ctrl-purple" onClick={onClose}>
          <FiDownload size={13} /> Push OTA
        </button>
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
        Devices to update:&nbsp;
        {devices.map((d, i) => (
          <span key={d.id}>{d.name}{i < devices.length - 1 ? ", " : ""}</span>
        ))}
      </div>
      <div style={{ marginTop: 8, padding: "6px 10px", background: "#fef3c7", borderRadius: 8, fontSize: 12, color: "#92400e" }}>
        ⚠ Devices will restart after firmware installation. Ensure they are not in critical operation.
      </div>
    </div>
  );
};

const BatchExportDialog = ({ selectedIds, allDevs, onClose }) => {
  const [fmt, setFmt] = useState("CSV");
  return (
    <div className="dvc-batch-dialog">
      <div className="dvc-batch-dialog-header">
        <FiPackage size={14} color="#f59e0b" />
        <span>Export {selectedIds.length} device{selectedIds.length > 1 ? "s" : ""}</span>
        <button className="dvc-close-btn" style={{ marginLeft: "auto" }} onClick={onClose}><FiX size={16} /></button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
        <label style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>Format:</label>
        {EXPORT_FORMATS.map(f => (
          <button key={f} onClick={() => setFmt(f)}
            style={{ padding: "4px 14px", borderRadius: 8, border: fmt === f ? "2px solid #3b82f6" : "1px solid #e5e7eb", background: fmt === f ? "#dbeafe" : "white", color: fmt === f ? "#1d4ed8" : "#374151", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
            {f}
          </button>
        ))}
        <button className="dvc-ctrl-btn dvc-ctrl-orange" style={{ marginLeft: "auto" }} onClick={onClose}>
          <FiDownload size={13} /> Download {fmt}
        </button>
      </div>
    </div>
  );
};

const BatchDeleteDialog = ({ selectedIds, allDevs, onClose, onConfirm }) => {
  const devices = allDevs.filter(d => selectedIds.includes(d.id));
  return (
    <div className="dvc-batch-dialog" style={{ borderColor: "#fca5a5" }}>
      <div className="dvc-batch-dialog-header">
        <FiTrash2 size={14} color="#ef4444" />
        <span style={{ color: "#ef4444" }}>Delete {selectedIds.length} device{selectedIds.length > 1 ? "s" : ""}</span>
        <button className="dvc-close-btn" style={{ marginLeft: "auto" }} onClick={onClose}><FiX size={16} /></button>
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: "#374151" }}>
        The following devices will be <strong>permanently removed</strong>:
      </div>
      <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
        {devices.map(d => (
          <span key={d.id} style={{ padding: "2px 8px", background: "#fee2e2", color: "#ef4444", borderRadius: 8, fontSize: 11, fontWeight: 600 }}>{d.name}</span>
        ))}
      </div>
      <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="dvc-ctrl-btn" style={{ background: "#f1f5f9", color: "#374151" }} onClick={onClose}>Cancel</button>
        <button className="dvc-ctrl-btn" style={{ background: "#ef4444", color: "white", border: "none" }} onClick={() => { onConfirm(); onClose(); }}>
          <FiTrash2 size={13} /> Confirm Delete
        </button>
      </div>
    </div>
  );
};

// ── Main Table Panel ──────────────────────────────────────────────────────────
const DvcTablePanel = ({ onSelectDevice, selectedDeviceId }) => {
  const { selectedSiteId, visibleSites } = useSite();

  const [search, setSearch]     = useState("");
  const [filterRole, setFilterRole]   = useState("All");
  const [filterConn, setFilterConn]   = useState("All");
  const [filterSol, setFilterSol]     = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [selectedRows, setSelectedRows] = useState([]);

  // Batch action dialog state
  const [batchDialog, setBatchDialog] = useState(null); // null | "assign" | "firmware" | "export" | "delete"
  const closeBatch = () => setBatchDialog(null);

  // Visible site IDs from context
  const visibleSiteIds = useMemo(() => {
    if (selectedSiteId === "all") return visibleSites.map(s => s.id);
    return [selectedSiteId];
  }, [selectedSiteId, visibleSites]);

  const filtered = useMemo(() => {
    return allDevices.filter(d => {
      // Global site filter from TopNavBar context
      if (d.siteId && !visibleSiteIds.includes(d.siteId)) return false;
      if (filterRole   !== "All" && d.role          !== filterRole)   return false;
      if (filterConn   !== "All" && d.connectivity  !== filterConn)   return false;
      if (filterSol    !== "All" && d.assignedSolution !== filterSol) return false;
      if (filterStatus !== "All" && d.status         !== filterStatus) return false;
      if (search) {
        const q = search.toLowerCase();
        return d.name.toLowerCase().includes(q) || d.id.toLowerCase().includes(q) || d.type.toLowerCase().includes(q) || (d.site || "").toLowerCase().includes(q);
      }
      return true;
    });
  }, [search, filterRole, filterConn, filterSol, filterStatus, visibleSiteIds]);

  const toggleRow = (id) => {
    setSelectedRows(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAll = () => {
    setSelectedRows(prev => prev.length === filtered.length ? [] : filtered.map(d => d.id));
  };

  return (
    <div className="dvc-table-layout">
      {/* Filter Bar */}
      <div className="dvc-filter-bar">
        <div className="dvc-search-wrap">
          <FiSearch size={14} color="#9ca3af" />
          <input className="dvc-search" placeholder="Search by name, ID, type, site…" value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af" }} onClick={() => setSearch("")}><FiX size={14} /></button>}
        </div>
        {selectedSiteId !== "all" && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 10px", background: "#ede9fe", borderRadius: 20, fontSize: 11, fontWeight: 700, color: "#7c3aed", flexShrink: 0 }}>
            <FiMapPin size={11} />
            {siteNameById(selectedSiteId)}
            <span style={{ color: "#a78bfa", fontWeight: 400 }}>· via Site Selector</span>
          </div>
        )}
        <div className="dvc-filter-group">
          <FiFilter size={13} color="#6b7280" />
          <select className="dvc-filter-sel" value={filterRole}   onChange={e => setFilterRole(e.target.value)}>
            {ALL_ROLES.map(r => <option key={r}>{r}</option>)}
          </select>
          <select className="dvc-filter-sel" value={filterConn}   onChange={e => setFilterConn(e.target.value)}>
            {ALL_CONN.map(c => <option key={c}>{c}</option>)}
          </select>
          <select className="dvc-filter-sel" value={filterSol}    onChange={e => setFilterSol(e.target.value)}>
            {ALL_SOLUTIONS.map(s => <option key={s}>{s}</option>)}
          </select>
          <select className="dvc-filter-sel" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            {ALL_STATUS.map(s => <option key={s}>{s}</option>)}
          </select>
          <button className="dvc-ctrl-btn dvc-ctrl-blue" style={{ gap: 5, padding: "5px 10px" }}>
            <FiDownload size={13} /> Export
          </button>
        </div>
      </div>

      {/* Batch Actions */}
      {selectedRows.length > 0 && (
        <>
          <div className="dvc-batch-bar">
            <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{selectedRows.length} selected</span>
            <button className={`dvc-ctrl-btn dvc-ctrl-blue ${batchDialog === "assign" ? "dvc-batch-btn-active" : ""}`} style={{ gap: 5 }}
              onClick={() => setBatchDialog(batchDialog === "assign" ? null : "assign")}>
              <FiMapPin size={13} /> Assign Solution
            </button>
            <button className={`dvc-ctrl-btn dvc-ctrl-purple ${batchDialog === "firmware" ? "dvc-batch-btn-active" : ""}`} style={{ gap: 5 }}
              onClick={() => setBatchDialog(batchDialog === "firmware" ? null : "firmware")}>
              <FiDownload size={13} /> Bulk Firmware Update
            </button>
            <button className={`dvc-ctrl-btn dvc-ctrl-orange ${batchDialog === "export" ? "dvc-batch-btn-active" : ""}`} style={{ gap: 5 }}
              onClick={() => setBatchDialog(batchDialog === "export" ? null : "export")}>
              <FiPackage size={13} /> Export Selected
            </button>
            <button className={`dvc-ctrl-btn ${batchDialog === "delete" ? "dvc-batch-btn-active" : ""}`}
              style={{ background: "#fee2e2", color: "#ef4444", border: "1px solid #fca5a5", gap: 5 }}
              onClick={() => setBatchDialog(batchDialog === "delete" ? null : "delete")}>
              <FiTrash2 size={13} /> Delete
            </button>
            <button style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af" }} onClick={() => { setSelectedRows([]); setBatchDialog(null); }}><FiX size={16} /></button>
          </div>

          {/* Inline batch dialogs — appear directly below the batch bar */}
          {batchDialog === "assign"   && <BatchAssignDialog   selectedIds={selectedRows} allDevs={allDevices} onClose={closeBatch} />}
          {batchDialog === "firmware" && <BatchFirmwareDialog selectedIds={selectedRows} allDevs={allDevices} onClose={closeBatch} />}
          {batchDialog === "export"   && <BatchExportDialog   selectedIds={selectedRows} allDevs={allDevices} onClose={closeBatch} />}
          {batchDialog === "delete"   && <BatchDeleteDialog   selectedIds={selectedRows} allDevs={allDevices} onClose={closeBatch} onConfirm={() => setSelectedRows([])} />}
        </>
      )}

      <div className="dvc-table-body">
        <div className="dvc-table-wrap">
          <table className="dvc-table">
            <thead>
              <tr>
                <th><input type="checkbox" checked={selectedRows.length === filtered.length && filtered.length > 0} onChange={toggleAll} /></th>
                <th>Device</th>
                <th>Role / Type</th>
                <th>Site</th>
                <th>Connectivity</th>
                <th>Status</th>
                <th>Health</th>
                <th>Battery</th>
                <th>Signal</th>
                <th>Solution</th>
                <th>Last Seen</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(dv => {
                const sm = statusMeta(dv.status);
                const cm = connMeta(dv.connectivity);
                const rm = roleMeta(dv.role);
                const sc = solColor(dv.assignedSolution);
                const hc = healthColor(dv.healthScore);
                const isSelected = dv.id === selectedDeviceId;
                return (
                  <tr key={dv.id} className={`dvc-tr ${isSelected ? "dvc-tr-selected" : ""}`}>
                    <td onClick={e => { e.stopPropagation(); toggleRow(dv.id); }}>
                      <input type="checkbox" checked={selectedRows.includes(dv.id)} readOnly />
                    </td>
                    <td>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{dv.name}</div>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>{dv.id}</div>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ width: 20, height: 20, borderRadius: "50%", background: rm.color, color: "white", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{rm.letter}</span>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: rm.color }}>{dv.role}</div>
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>{dv.type}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <FiMapPin size={11} color="#6b7280" />
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", whiteSpace: "nowrap", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {dv.siteId ? siteNameById(dv.siteId) : (dv.site || "—")}
                          </div>
                          <div style={{ fontSize: 10, color: "#9ca3af", fontFamily: "monospace" }}>{dv.siteId || ""}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <cm.Icon size={14} color={cm.color} />
                        <span style={{ fontSize: 12, color: cm.color, fontWeight: 600 }}>{dv.connectivity}</span>
                      </div>
                    </td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 12, background: sm.bg, color: sm.color, fontSize: 11, fontWeight: 700 }}>
                        <sm.icon size={11} />{dv.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 36, height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ width: `${dv.healthScore}%`, height: "100%", background: hc, borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: hc }}>{dv.healthScore}</span>
                      </div>
                    </td>
                    <td><BatteryBar pct={dv.battery} /></td>
                    <td><SignalBars rssi={dv.signal} /></td>
                    <td>
                      {dv.assignedSolution && dv.assignedSolution !== "None" && (
                        <span style={{ padding: "2px 8px", borderRadius: 12, background: sc.bg, color: sc.color, fontSize: 11, fontWeight: 700 }}>{dv.assignedSolution}</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#6b7280" }}>
                        <FiClock size={11} />{dv.lastSeen.split(",")[1]?.trim() || dv.lastSeen}
                      </div>
                    </td>
                    <td>
                      <button className="dvc-ctrl-btn dvc-ctrl-purple" style={{ fontSize: 11, padding: "4px 10px", gap: 4 }}
                        onClick={() => onSelectDevice && onSelectDevice(dv)}>
                        <FiChevronRight size={12} /> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="dvc-empty" style={{ padding: 40 }}>No devices match current filters</div>
          )}
          <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 12px" }}>
            Showing {filtered.length} of {allDevices.length} devices — click <strong>View</strong> to open Device Detail tab
          </div>
        </div>
      </div>
    </div>
  );
};

export default DvcTablePanel;
